import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation, useNavigate } from "react-router";
import { z } from "zod";

import { AuthCard } from "@/app/layouts/AuthLayout";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { ApiError } from "@/lib/api/client";
import { canViewAdmin } from "@/lib/auth/access";
import { useAuth } from "@/lib/auth/AuthProvider";
import { clearSignedOutAfter, readSignedOutAfter } from "@/lib/auth/device";

const schema = z.object({
  email: z.string().min(1, "Enter your email address.").email("Enter a valid email address."),
  password: z.string().min(1, "Enter your password."),
  remember_me: z.boolean(),
});

type Values = z.infer<typeof schema>;

/** Where each account status belongs once signed in. */
const HOME_FOR_STATUS: Record<string, string> = {
  draft: "/onboarding",
  pending: "/pending",
  rejected: "/rejected",
};

export function SignInRoute() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);

  // Why this page is showing, if a tab signed itself out for being idle. Read
  // once as the page opens and cleared after, so the next person at the
  // tablet sees it and the person after them does not.
  const [signedOutAfter] = useState(readSignedOutAfter);
  useEffect(() => {
    clearSignedOutAfter();
  }, []);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    // Off by default: the counter tablets are shared, and a sign-in that
    // outlives the tab is something to ask for on a device of one's own.
    // The address is filled in when the person came from registering with
    // one that already has an account.
    defaultValues: {
      email: (location.state as { email?: string } | null)?.email ?? "",
      password: "",
      remember_me: false,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      const session = await signIn(values.email, values.password, values.remember_me);
      // A password HR gave out is replaced before anything else.
      if (session.must_change_password) {
        navigate("/choose-password", { replace: true });
        return;
      }
      const intended = (location.state as { from?: string } | null)?.from;
      const statusHome = HOME_FOR_STATUS[session.user.status];
      const fallback = canViewAdmin(session) ? "/admin" : "/home";
      navigate(statusHome ?? intended ?? fallback, { replace: true });
    } catch (error) {
      setFormError(
        error instanceof ApiError
          ? error.message
          : "Could not reach the server. Check your connection and try again.",
      );
    }
  });

  return (
    <AuthCard
      title="Sign in"
      description="Use the email address you registered with."
      footer={
        <>
          No account yet?{" "}
          <Link to="/register" className="font-medium text-accent hover:underline">
            Register
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {signedOutAfter ? (
          <p className="text-xs text-fg-subtle">
            You were signed out after {signedOutAfter} minutes without activity.
          </p>
        ) : null}

        {formError ? <Alert>{formError}</Alert> : null}

        <Field label="Email address" error={form.formState.errors.email?.message}>
          <Input
            type="email"
            autoComplete="username"
            autoFocus
            placeholder="you@firstgulfexchange.com"
            {...form.register("email")}
          />
        </Field>

        <Field label="Password" error={form.formState.errors.password?.message}>
          <Input type="password" autoComplete="current-password" {...form.register("password")} />
        </Field>

        <label className="flex cursor-pointer items-start gap-3 text-base text-fg">
          <input
            type="checkbox"
            className="mt-1 size-4 shrink-0 accent-accent"
            {...form.register("remember_me")}
          />
          <span className="min-w-0">
            <span className="block">Keep me signed in on this device</span>
            <span className="block text-xs text-fg-subtle">
              Leave it off on a shared counter tablet: the sign-in then ends with the tab.
            </span>
          </span>
        </label>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={form.formState.isSubmitting}
        >
          Sign in
        </Button>

        <p className="text-center">
          <Link to="/forgot-password" className="text-base text-fg-muted hover:text-accent">
            Forgot your password?
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}
